import { createAsyncThunk } from '@reduxjs/toolkit';
import { ResourceIdParamName, ResourceIdType } from 'src/types/ResourceId';
import { getQueryParams, getSkjemaNameFromUrl, type QueryStrings } from 'src/util';
import { getLanguagesInDefinition } from 'src/util/getLanguagesInDefinition';
import { type LanguageLocales, portalLanguages } from 'src/util/portalLanguage-utils';
import { trackFormEvent } from 'src/util/trackFormEvent';

import type { SkjemaDefinisjonLoadedPayload } from './actions';
import type { AppDispatch, RootState } from '../reducers';
import type { Bundle, Questionnaire } from 'fhir/r4';
import type { GetSkjemaResponse, GetSkjemasvarResponse, PostSkjemaResponse } from 'src/types/MinHelseEntities';

import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';
import { erTjenester, get, post } from '@helsenorge/framework-utils/hn-proxy-service';
import { allowedLanguagesReplaced } from '@helsenorge/framework-utils/language';

import { setMainQuestionnaire } from './actionCreators';
import { getContextQueryParametersAsJson, getLoadParameter, getQuestionnairs, hasContextParameters } from './actionUtils';
import { type ProxyErrorResponse, transformFetchErrors } from './errorHandling';

type ProcessApiResponseParams = {
  skjemaNavn?: string;
  dispatch: AppDispatch;
  fulfillWithValue: (payload: SkjemaDefinisjonLoadedPayload) => SkjemaDefinisjonLoadedPayload;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rejectWithValue: (value: ProxyErrorResponse | string) => any;
  getState: () => RootState;
};
async function processApiResponse(
  response: GetSkjemaResponse | PostSkjemaResponse | GetSkjemasvarResponse,
  { skjemaNavn, dispatch, fulfillWithValue, rejectWithValue }: ProcessApiResponseParams
): Promise<SkjemaDefinisjonLoadedPayload> {
  const { Metadata: metadata, WarningMessage: warning, Questionnaire: data } = response;

  const questionnaireResponse = 'QuestionnaireResponse' in response ? response.QuestionnaireResponse : undefined;
  const availableLanguages = 'AvailableLanguages' in response && response.AvailableLanguages ? response.AvailableLanguages : [];

  const queryParams = getQueryParams();
  trackFormEvent('form continue', skjemaNavn);

  let parsedData: Bundle | Questionnaire;
  try {
    parsedData = JSON.parse(data);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return rejectWithValue('Feil i json struktur');
  }

  const questionnaires = getQuestionnairs(parsedData);
  if (!questionnaires) {
    return rejectWithValue('oppgavenFinsIkke');
  }
  const languages = availableLanguages.length > 0 ? availableLanguages : getLanguagesInDefinition(questionnaires);
  await dispatch(allowedLanguagesReplaced(languages.filter(lang => portalLanguages.includes(lang))));

  await dispatch(setMainQuestionnaire({ questionnaires, metadata, questionnaireResponse, availableLanguages: languages }));

  return fulfillWithValue({
    questionnaires,
    userMetadata: metadata,
    warning,
    taskId: queryParams.get('taskId') ?? undefined,
    returUrl: queryParams.get('returURL') ?? undefined,
    dokumentGuid: queryParams.get('contextId') ?? undefined,
    qr: questionnaireResponse,
    availableLanguages: languages,
  });
}

export type ApiCallConfig = {
  endpoint: string;
  params: Record<string, string | undefined | { [key: string]: unknown }> | URLSearchParams | QueryStrings;
  verb: 'get' | 'post' | 'put' | 'delete';
};

const getResourceIdParamName = (params: URLSearchParams): string => {
  const resourceIdType = params.get('resourceIdType');

  switch (resourceIdType) {
    case ResourceIdType.Blankettnummer:
      return ResourceIdParamName.Name;
    case ResourceIdType.SkjemaId:
      return ResourceIdParamName.Id;
    default:
      return ResourceIdParamName.Id;
  }
};

export const fetchSkjemaoppgave = createAsyncThunk<
  SkjemaDefinisjonLoadedPayload,
  { language: LanguageLocales | undefined },
  { state: RootState; dispatch: AppDispatch; rejectValue: ProxyErrorResponse | string }
>(
  'fetchSkjemaoppgave',
  async ({ language }, thunkAPI) => {
    const { rejectWithValue, getState, fulfillWithValue, dispatch } = thunkAPI;
    const skjemaTekniskNavn = getState().microweb.skjemaTekniskNavn;
    const documentGuid = getState().microweb.documentGuid;
    const fetchLanguage = language ?? getState().language.currentLanguage;

    const isMicroweb = getState().microweb.isMicroweb;
    const isMicrowebOppgave = getState().microweb.isMicrowebOppgave;
    const taskId = getState().microweb.taskId;
    const params = getQueryParams();
    const authorized = isAuthorized();
    const tjenester = erTjenester();
    const skjemaNavn = (isMicroweb && skjemaTekniskNavn) || getSkjemaNameFromUrl();
    const effectiveDocumentGuid = isMicroweb ? documentGuid : params?.get('contextId');
    const effectiveTaskId = (isMicrowebOppgave && taskId) || params?.get('taskId');
    const hasContextParams = hasContextParameters(params);
    const hasResourceIdParam = params?.has('resourceId');

    let apiCallConfig: ApiCallConfig | null = null;
    if (effectiveDocumentGuid && authorized) {
      // Case 1: Fetch a saved form instance (Skjemasvar)

      apiCallConfig = {
        endpoint: 'api/v1/Skjemasvar',
        params: {
          ...(params?.has('taskId') && { OppgaveGuid: params?.get('taskId') ?? undefined }),
          DokumentGuid: effectiveDocumentGuid,
          Language: fetchLanguage,
        },
        verb: 'get',
      };
    } else if (effectiveTaskId && authorized) {
      // Case 2: Fetch a form definition by task ID (Skjema)

      const contextParameters = getContextQueryParametersAsJson(params);
      const resourceIdParamName = getResourceIdParamName(params);

      apiCallConfig = {
        endpoint: `api/v1/Skjema${hasContextParams ? '/SetContextParameters' : ''}`,
        params: {
          ...(hasResourceIdParam && { [resourceIdParamName]: params.get('resourceId') ?? undefined }),
          ...(skjemaNavn && { name: skjemaNavn ?? undefined }),
          OppgaveGuid: effectiveTaskId,
          ...(hasContextParams && { ContextParameters: contextParameters }),
          Language: fetchLanguage,
        },
        verb: hasContextParams ? 'post' : 'get',
      };
    } else if (params?.has('Query') || skjemaNavn) {
      // Case 3: Fetch a new form definition by name/query
      const contextParameters = getContextQueryParametersAsJson(params);

      const [loadParamName, loadParamValue] = getLoadParameter(params?.get('Query') || '', skjemaNavn);
      apiCallConfig = {
        endpoint:
          authorized && tjenester
            ? `api/v1/Skjema${hasContextParams ? '/SetContextParameters' : ''}`
            : `api/v1/SkjemaAnonym${hasContextParams ? '/SetContextParameters' : ''}`,
        params: {
          [loadParamName]: loadParamValue,
          ...(hasContextParams && { ContextParameters: contextParameters }),
          Language: fetchLanguage,
        },
        verb: hasContextParams ? 'post' : 'get',
      };
    }
    if (!apiCallConfig) {
      return rejectWithValue('Kunne ikke hente skjema. Mangler nødvendige parametere');
    }
    try {
      let response: GetSkjemaResponse | GetSkjemasvarResponse | PostSkjemaResponse | null;
      if (apiCallConfig.verb === 'post') {
        // Use post for 'api/v1/Skjema' or 'api/v1/SkjemaAnonym' when context params are present
        response = await post<PostSkjemaResponse, QueryStrings>(
          'skjemainternal',
          apiCallConfig.endpoint,
          apiCallConfig.params as QueryStrings
        );
      } else {
        // All other cases use get
        response = await get<GetSkjemaResponse | GetSkjemasvarResponse>('skjemainternal', apiCallConfig.endpoint, apiCallConfig.params);
      }
      if (!response) {
        return rejectWithValue('No response from server');
      }
      return await processApiResponse(response, {
        skjemaNavn,
        dispatch,
        fulfillWithValue,
        rejectWithValue,
        getState,
      });
    } catch (error) {
      const errorInfo = transformFetchErrors(error);
      return rejectWithValue(errorInfo);
    }
  },
  {
    condition: ({ language }, { getState }) => {
      const state = getState();
      const { skjemaTekniskNavn, isMicroweb } = state.microweb;
      const targetSkjemaNavn = isMicroweb && skjemaTekniskNavn ? skjemaTekniskNavn : getSkjemaNameFromUrl();
      const { questionnaires, showLocalSpinner } = state.refero.ui;

      if (showLocalSpinner) {
        return false;
      }
      const hasCachedData = questionnaires?.some(q => q.name === targetSkjemaNavn && q.language === language);

      if (hasCachedData) {
        return false;
      }
      return true;
    },
  }
);
